import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type TextAnchor,
  createTextAnchor,
  getSelectionRange,
  clearSelection as clearWindowSelection,
  serializeTextAnchor,
} from '../utils/textAnchor';

/**
 * Text selection with TextAnchor creation
 *
 * This hook tracks user text selection and creates precise TextAnchors
 * for highlights, bookmarks, and flashcards
 */
export function useTextAnchorSelection(containerRef: React.RefObject<HTMLElement>) {
  const [selectedText, setSelectedText] = useState<string>('');
  const [textAnchor, setTextAnchor] = useState<TextAnchor | null>(null);
  const [selectionRange, setSelectionRange] = useState<Range | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);

  /**
   * Handle text selection
   */
  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setSelectedText('');
      setTextAnchor(null);
      setSelectionRange(null);
      setMenuPosition(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const text = range.toString().trim();

    // Ignore empty selections
    if (!text || text.length === 0) {
      setSelectedText('');
      setTextAnchor(null);
      setSelectionRange(null);
      setMenuPosition(null);
      return;
    }

    // Ensure selection is within our container
    if (containerRef.current) {
      if (!containerRef.current.contains(range.commonAncestorContainer)) {
        return;
      }
    }

    // Create TextAnchor for this selection
    const root = containerRef.current || document.body;
    const anchor = createTextAnchor(range, root);

    // Calculate menu position with smart positioning (above or below based on available space)
    const rect = range.getBoundingClientRect();
    const MENU_HEIGHT = 350; // Estimated menu height (conservative)
    const HEADER_HEIGHT = 90; // Approximate header height
    const SAFE_MARGIN = 20; // Extra space buffer

    // Calculate actual available space above the selection (accounting for header)
    const spaceAbove = rect.top - HEADER_HEIGHT;
    const hasSpaceAbove = spaceAbove >= (MENU_HEIGHT + SAFE_MARGIN);

    // Also check if positioning below would go off-screen
    const spaceBelow = window.innerHeight - rect.bottom;
    const hasSpaceBelow = spaceBelow >= (MENU_HEIGHT + SAFE_MARGIN);

    // Prefer above, but use below if not enough space above
    const positionBelow = !hasSpaceAbove || (hasSpaceBelow && spaceBelow > spaceAbove);

    // Use negative Y to signal "show below" to the menu component
    const yPosition = positionBelow ? rect.bottom + 10 : rect.top - 10;

    const position = {
      x: rect.left + rect.width / 2,
      y: positionBelow ? -yPosition : yPosition, // Negative Y signals "show below"
    };

    setSelectedText(text);
    setTextAnchor(anchor);
    setSelectionRange(range);
    setMenuPosition(position);
  }, [containerRef]);

  /**
   * Clear current selection
   */
  const clearSelection = useCallback(() => {
    clearWindowSelection();
    setSelectedText('');
    setTextAnchor(null);
    setSelectionRange(null);
    setMenuPosition(null);
  }, []);

  /**
   * Get serialized anchor for backend storage
   */
  const getSerializedAnchor = useCallback((): string | null => {
    if (!textAnchor) {
      return null;
    }
    return serializeTextAnchor(textAnchor);
  }, [textAnchor]);

  /**
   * Listen to selection changes
   */
  useEffect(() => {
    // Debounce selection handling to avoid excessive updates
    let timeoutId: NodeJS.Timeout;

    const handleSelectionChange = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleSelection, 100);
    };

    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelection]);

  /**
   * Clear selection when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't clear if clicking on selection menu or highlights
      if (
        target.closest('.text-selection-menu') ||
        target.closest('.reader-highlight')
      ) {
        return;
      }

      clearSelection();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clearSelection]);

  return {
    selectedText,
    textAnchor,
    selectionRange,
    menuPosition,
    clearSelection,
    getSerializedAnchor,
  };
}
